import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import BdsButton from '../../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import CountUpNumber from '../../../../../../shared/components/v3/CountUpNumber/CountUpNumber';
import { CREATE_CHALLENGE_ROUTE, NEW_STORY_ROUTE } from '../../../../../../shared/constants/auth';
import { CLIENTS_PORTAL_URL } from '../../../../../../shared/constants/constants';
import { COUNT_UP_DURATION, WELLBEING_INFO_TYPES } from '../../../../../../shared/constants/reports';
import { CLICK_ACTION, HEALTH_REPORT_HABITS_AND_BENEFITS } from '../../../../../../shared/constants/tagManager';
import useInView from '../../../../../../shared/hooks/useInView/useInView';
import { IHealthReport } from '../../../../../../shared/interfaces/reports';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_REPORTS } from '../../../../../../shared/translations/reports';

import './WellbeingSummaryData.scss';

type StoryType = typeof WELLBEING_INFO_TYPES.medicalCheckUp | typeof WELLBEING_INFO_TYPES.sleepProblems;
type InfoType = StoryType | typeof WELLBEING_INFO_TYPES.challenges;

export const WellbeingSummaryData = ({
  data,
  isLastReport,
  inDownloadMode
}: {
  data: IHealthReport;
  isLastReport?: boolean;
  inDownloadMode?: boolean;
}) => {
  const { t } = useTranslation();
  const { ref: containerRef, enterCount: cycle } = useInView<HTMLDivElement>({
    threshold: 0.3,
    enabled: !inDownloadMode
  });

  const info = useMemo(() => {
    const rules = [
      {
        condition: data.medicalCheckoutPercentage <= 30,
        titleKey: TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.preventiveCheckUp.title,
        descKey: TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.preventiveCheckUp.description,
        percentage: data.medicalCheckoutPercentage,
        type: WELLBEING_INFO_TYPES.medicalCheckUp
      },
      {
        condition: data.physicalActivityPercentage <= 20,
        titleKey: TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.physicalActivity.title,
        descKey: TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.physicalActivity.description,
        percentage: data.physicalActivityPercentage,
        type: WELLBEING_INFO_TYPES.challenges
      },
      {
        condition: data.stressedUsersPercentage >= 70,
        titleKey: TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.stress.title,
        descKey: TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.stress.description,
        percentage: data.stressedUsersPercentage,
        type: WELLBEING_INFO_TYPES.challenges
      },
      {
        condition: data.sleepQualityPercentage >= 40,
        titleKey: TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.difficultySleeping.title,
        descKey: TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.difficultySleeping.description,
        percentage: data.sleepQualityPercentage,
        type: WELLBEING_INFO_TYPES.sleepProblems
      }
    ];

    const matched = rules.find((r) => r.condition);
    if (matched) {
      return {
        title: t(matched.titleKey),
        description: t(matched.descKey),
        percentage: matched.percentage,
        type: matched.type as InfoType,
        buttonText:
          matched.type === WELLBEING_INFO_TYPES.challenges
            ? t(TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.challengesTextButton)
            : t(TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.storiesTextButton)
      };
    }

    return {
      title: t(TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.difficultySleeping.title),
      description: t(TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.difficultySleeping.description),
      percentage: data.sleepQualityPercentage,
      type: WELLBEING_INFO_TYPES.sleepProblems,
      buttonText: t(TRANSLATIONS_REPORTS.healthReport.wellbeingSummaryData.storiesTextButton)
    };
  }, [data, t]);

  const redirectAction = () => {
    TagManagerService.pushEvent(CLICK_ACTION, HEALTH_REPORT_HABITS_AND_BENEFITS, { type: info.type });
    if (info.type === WELLBEING_INFO_TYPES.challenges) {
      window.open(CLIENTS_PORTAL_URL + CREATE_CHALLENGE_ROUTE, '_blank');
      return;
    }

    const hasStoryImage =
      info.type === WELLBEING_INFO_TYPES.medicalCheckUp || info.type === WELLBEING_INFO_TYPES.sleepProblems;
    const target = hasStoryImage ? `${NEW_STORY_ROUTE}?type=${info.type}` : NEW_STORY_ROUTE;
    window.open(CLIENTS_PORTAL_URL + target, '_blank');
  };

  return (
    <div className="wellbeing-summary-data-container" ref={containerRef}>
      <p className="wsd-title">
        <span className="wsd-percentage">
          {inDownloadMode ? (
            `${info.percentage}%`
          ) : (
            <CountUpNumber key={`wsd-${cycle}`} value={info.percentage} duration={COUNT_UP_DURATION} suffix="%" />
          )}
        </span>
        {` ${info.title}`}
      </p>
      <p className="health-report-description">{info.description}</p>
      {isLastReport && (
        <div className="only-landing">
          <BdsButton variant="quaternary" size="s" action={() => redirectAction()}>
            {info.buttonText}
          </BdsButton>
        </div>
      )}
    </div>
  );
};

export default WellbeingSummaryData;
